import type { ConsentRecord } from "@/tracking/consent/consentTypes";

/**
 * Reports a consent decision to the server, so the choice is documentable somewhere other than the
 * visitor's own browser.
 *
 * <p>A receipt held only in a cookie proves nothing: the visitor can clear it, and we would have no
 * record that a decision was ever made. The stored copy is what a later server-side conversion is
 * checked against — the dispatcher looks the receipt up by id and skips any destination the
 * categories do not cover.</p>
 *
 * <p>Deliberately minimal. The id, the policy version, the two flags, the tenant and where the
 * decision was made. No visitor identifier, and the server records the hostname it saw rather than
 * one the browser asserts. The IP the request arrives on is not stored.</p>
 */
export function postConsentReceipt(record: ConsentRecord): void {
  const body = JSON.stringify({
    consentReceiptId: record.consentReceiptId,
    policyVersion: record.policyVersion,
    decidedAt: record.decidedAt,
    statistics: record.categories.statistics,
    marketing: record.categories.marketing,
    tenant: record.tenant,
    source: record.source,
  });

  try {
    // keepalive, because the decision is frequently the last thing that happens before the visitor
    // navigates — "Godta alle" and then straight on. Without it the request dies with the document.
    void fetch("/api/tracking/consent-receipt", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      // The receipt is not a credential and carries nothing that identifies the visitor, so it does
      // not need the session — and sending it without cookies keeps it out of any authenticated log.
      credentials: "omit",
    }).catch(() => {
      // Swallowed on purpose. An ad blocker may refuse the request outright, and a deployment that
      // has not been migrated has nowhere to put the row, and neither is a reason to disturb someone
      // who has just made a choice. The cookie is already written; this is the durable copy, not the
      // decision itself.
    });
  } catch {
    // fetch itself can throw synchronously where the API is absent or stubbed, as in jsdom.
  }
}
